import Header from "components/Header";
import PortalMessages from "components/PortalMessages";
import Context from "context/Global";
import React, { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import Badge from "./Badge";
import Alert, { AlertTypes } from "../../components/Alerts/Alert";
import { EXPIRED } from "../../constants/integrations/IntegrationStatuses";

let Container = styled.div`
    width: 100%;
    max-width: 100%;
    min-height: 100%;

    > .c-header {
        position: fixed;
        top: 0;
        left: 0;
        height: 60px;
        width: 100%;
        background: #fff;
        z-index: 50;
    }
`;

let MenuWrapper = styled.div`
    width: 300px;
    height: 100%;
    padding-top: 60px;
    position: fixed;
    display: flex;
    flex-direction: column;
    background: #fff;
    z-index: 9;

    > .top {
        overflow: auto;
    }

    > .bottom {
        border-top: 1px solid rgba(0, 0, 0, 0.05);
    }

    @media (max-width: 700px) {
        top: calc(100vh - 60px);
        bottom: 0;
        left: 0;
        padding-top: 0;
        min-width: 100%;
        width: 100vw;
        height: 60px;
        flex-direction: row;
    }
`;

let Content = styled.div`
    padding-top: 60px;
    padding-left: 300px;
    width: 100%;
    height: 100vh;
    min-height: 100vh;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;

    @media (max-width: 700px) {
        padding-left: 0;
    }

    > .title {
        padding: 1rem;
        font-size: 2rem;
    }

    > .text {
        padding: 0 1rem;
    }
`;

let Item = styled.a`
    width: 100%;
    padding: 0.8em 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: row;
    align-items: center;
    text-decoration: inherit;
    color: inherit;
    font-style: inherit;
    font-size: inherit;
    transition: all 0.3s ease;
    background: #fff;
    color: rgba(0, 0, 0, 0.8);
    cursor: pointer;
    position: relative;

    > .badge {
        position: absolute;
        border-radius: 200px;
        height: 16px;
        width: 16px;
        box-sizing: border-box;
        top: 8px;
        right: calc(100% - 46px);
        display: flex;
        justify-content: center;
        align-items: center;
    }

    &:hover {
        text-decoration: none;
        color: rgba(0, 0, 0, 1);
        background: var(--menu-item--hover, #fafafa);
    }

    &.is-selected {
        color: rgba(0, 0, 0, 1);
        background: var(--menu-item--selected, #eee);
    }

    > .icon {
        padding: 0 8px;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 48px;
        height: 32px;
        min-height: 32px;
        color: inherit;
        box-sizing: border-box;
        flex-shrink: 0;
    }

    > .health {
        display: flex;
        justify-content: space-around;
        width: 100%;
        height: 100%;
        align-items: center;

        > .service {
            > .light {
                width: 12px;
                height: 12px;
                border-radius: 8px;

                &.is-green {
                    background: rgba(0, 200, 0, 1);
                    border: 1px solid rgba(150, 255, 150, 1);
                }
                &.is-orange {
                    background: orange;
                    background: rgba(255, 100, 0, 1);
                    border: 1px solid rgba(255, 200, 150, 1);
                }
                &.is-red {
                    background: rgba(200, 0, 0, 1);
                    border: 1px solid rgba(255, 150, 150, 1);
                }
            }
        }
    }

    > .title {
        display: flex;
        justify-content: flex-start;
        align-items: center;
    }

    @media (max-width: 700px) {
        width: 100%;
        height: 100%;
        justify-content: center;
        flex-direction: column;
        padding: 0;

        > .badge {
            top: 6px;
            left: calc(50% + 6px);
        }

        > .title {
            display: flex;
            font-size: 10px;
            margin-top: 0;
            text-align: center;
            padding: 0 8px;
            line-height: 10px;
        }
    }
`;

function LoggedIn(props) {
    const context = useContext(Context);
    const [menuItems, setMenuitems] = useState([]);
    const [bottomItems, setBottomItems] = useState([]);
    const { t, i18n } = useTranslation();

    useEffect(() => {
        let adminViewUrl = "";

        if (context.user.role === "OWNER") {
            adminViewUrl = "/admin/dashboard";
        } else if (context.user.role === "CLIENT_MANAGER") {
            adminViewUrl = "/admin/sales";
        } else if (context.user.role === "SUPPORT") {
            adminViewUrl = "/admin/support";
        }

        let items = [
            {
                title: t("menu.option.new-consignments"),
                icon: (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M14.039 8.968l-3.961-3.968h1.968s.991-3.178-2.985-5c6.191.222 6.972 5 6.972 5h1.967l-3.961 3.968zm6.461 5.575l-.342.195v1.717l.342-.195v-1.717zm-.844.481l-.35.199v1.717l.35-.199v-1.717zm.906-10.024l-1.306 1.55 1.62.919-6.859 3.698-8.86-5.138 4.436-2.685c-.234-.5-.626-.989-1.313-1.338l-5.124 2.978-3.156 5.487 2 1.106v5.445l12.25 6.978 9.75-5.551v-11.508l-3.438-1.941zm-16.624 2.688l7.897 4.54-1.192 2.091-7.872-4.605 1.167-2.026zm9.062 13.298l-9-5.126v-3.112l7.377 4.319 1.623-2.845v6.764zm9-3.7l-7 3.985v-8.361l7-3.773v8.149zm-4.907-.802l-.349.199v1.713l.349-.195v-1.717zm.584-.333l-.343.195v1.717l.343-.195v-1.717zm1.395-.794l-.343.195v1.717l.343-.195v-1.717zm-.574.327l-.344.196v1.717l.344-.196v-1.717z"
                        />
                    </svg>
                ),
                url: "/create",
                onClick: null,
            },
        ];

        items.push({
            title: "Batch",
            icon: (
                <svg
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <mask
                        id="mask0_35_61"
                        style={{ maskType: "alpha" }}
                        maskUnits="userSpaceOnUse"
                        x="0"
                        y="0"
                        width="30"
                        height="30"
                    >
                        <rect width="30" height="30" fill="#D9D9D9" />
                    </mask>
                    <g mask="url(#mask0_35_61)">
                        <path
                            d="M7.01922 25C6.44391 25 5.96354 24.8073 5.57812 24.4219C5.19271 24.0365 5 23.5561 5 22.9808V7.01922C5 6.44391 5.19271 5.96354 5.57812 5.57812C5.96354 5.19271 6.44391 5 7.01922 5H22.9808C23.5561 5 24.0365 5.19271 24.4219 5.57812C24.8073 5.96354 25 6.44391 25 7.01922V17.4255C24.8013 17.3405 24.6018 17.2652 24.4014 17.1995C24.2011 17.1338 23.984 17.0753 23.75 17.024V7.01922C23.75 6.82693 23.6699 6.65065 23.5096 6.49038C23.3494 6.33013 23.1731 6.25 22.9808 6.25H7.01922C6.82693 6.25 6.65065 6.33013 6.49038 6.49038C6.33013 6.65065 6.25 6.82693 6.25 7.01922V22.9808C6.25 23.1731 6.33013 23.3494 6.49038 23.5096C6.65065 23.6699 6.82693 23.75 7.01922 23.75H15.5288C15.5288 23.9631 15.5398 24.1748 15.5616 24.3851C15.5834 24.5953 15.6198 24.8003 15.6707 25H7.01922ZM10.1459 20.6731C10.4188 20.6731 10.647 20.5808 10.8305 20.3962C11.014 20.2115 11.1058 19.9828 11.1058 19.7098C11.1058 19.4369 11.0135 19.2087 10.8288 19.0253C10.6442 18.8418 10.4154 18.75 10.1425 18.75C9.86961 18.75 9.64142 18.8423 9.45794 19.0269C9.27444 19.2115 9.18269 19.4403 9.18269 19.7132C9.18269 19.9861 9.275 20.2143 9.45963 20.3978C9.64423 20.5813 9.87299 20.6731 10.1459 20.6731ZM10.1459 15.9615C10.4188 15.9615 10.647 15.8692 10.8305 15.6846C11.014 15.5 11.1058 15.2712 11.1058 14.9983C11.1058 14.7254 11.0135 14.4972 10.8288 14.3137C10.6442 14.1302 10.4154 14.0385 10.1425 14.0385C9.86961 14.0385 9.64142 14.1308 9.45794 14.3154C9.27444 14.5 9.18269 14.7288 9.18269 15.0017C9.18269 15.2746 9.275 15.5028 9.45963 15.6863C9.64423 15.8698 9.87299 15.9615 10.1459 15.9615ZM10.1459 11.25C10.4188 11.25 10.647 11.1577 10.8305 10.9731C11.014 10.7885 11.1058 10.5597 11.1058 10.2868C11.1058 10.0139 11.0135 9.78566 10.8288 9.60216C10.6442 9.41866 10.4154 9.32691 10.1425 9.32691C9.86961 9.32691 9.64142 9.41922 9.45794 9.60384C9.27444 9.78847 9.18269 10.0172 9.18269 10.2902C9.18269 10.5631 9.275 10.7913 9.45963 10.9748C9.64423 11.1583 9.87299 11.25 10.1459 11.25ZM20.0962 15.625C20.2732 15.625 20.4217 15.565 20.5415 15.4451C20.6613 15.3251 20.7212 15.1765 20.7212 14.9992C20.7212 14.8218 20.6613 14.6735 20.5415 14.5541C20.4217 14.4347 20.2732 14.375 20.0962 14.375H14.6154C14.4383 14.375 14.2899 14.435 14.1701 14.5549C14.0503 14.6749 13.9904 14.8235 13.9904 15.0008C13.9904 15.1782 14.0503 15.3265 14.1701 15.4459C14.2899 15.5653 14.4383 15.625 14.6154 15.625H20.0962ZM20.0962 10.9135C20.2732 10.9135 20.4217 10.8535 20.5415 10.7335C20.6613 10.6136 20.7212 10.4649 20.7212 10.2876C20.7212 10.1103 20.6613 9.96193 20.5415 9.84253C20.4217 9.72316 20.2732 9.66347 20.0962 9.66347H14.6154C14.4383 9.66347 14.2899 9.72344 14.1701 9.84337C14.0503 9.96333 13.9904 10.112 13.9904 10.2893C13.9904 10.4666 14.0503 10.615 14.1701 10.7344C14.2899 10.8538 14.4383 10.9135 14.6154 10.9135H20.0962ZM16.4952 20.3365C16.6138 20.0978 16.746 19.8782 16.8918 19.6779C17.0376 19.4776 17.1971 19.2804 17.3702 19.0865H14.6154C14.4383 19.0865 14.2899 19.1465 14.1701 19.2665C14.0503 19.3864 13.9904 19.5351 13.9904 19.7124C13.9904 19.8897 14.0503 20.0381 14.1701 20.1575C14.2899 20.2768 14.4383 20.3365 14.6154 20.3365H16.4952ZM21.7788 24.375H18.6538C18.4768 24.375 18.3283 24.315 18.2085 24.1951C18.0887 24.0751 18.0288 23.9265 18.0288 23.7492C18.0288 23.5718 18.0887 23.4235 18.2085 23.3041C18.3283 23.1847 18.4768 23.125 18.6538 23.125H21.7788V20C21.7788 19.8229 21.8388 19.6745 21.9588 19.5547C22.0787 19.4349 22.2274 19.375 22.4047 19.375C22.582 19.375 22.7304 19.4349 22.8498 19.5547C22.9691 19.6745 23.0288 19.8229 23.0288 20V23.125H26.1538C26.3309 23.125 26.4794 23.185 26.5992 23.3049C26.7189 23.4249 26.7788 23.5735 26.7788 23.7508C26.7788 23.9282 26.7189 24.0765 26.5992 24.1959C26.4794 24.3153 26.3309 24.375 26.1538 24.375H23.0288V27.5C23.0288 27.6771 22.9689 27.8255 22.8489 27.9453C22.729 28.0651 22.5803 28.125 22.403 28.125C22.2257 28.125 22.0773 28.0651 21.9579 27.9453C21.8385 27.8255 21.7788 27.6771 21.7788 27.5V24.375Z"
                            fill="#000000"
                        />
                    </g>
                </svg>
            ),
            url: "/batch",
            onClick: null,
        });

        if (context.user.customer.settings.drafts) {
            items.push({
                title: t("menu.option.saved-consignments"),
                badge: context.user.draftCount || null,
                icon: (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M10 19.5c0 .829-.672 1.5-1.5 1.5s-1.5-.671-1.5-1.5c0-.828.672-1.5 1.5-1.5s1.5.672 1.5 1.5zm3.5-1.5c-.828 0-1.5.671-1.5 1.5s.672 1.5 1.5 1.5 1.5-.671 1.5-1.5c0-.828-.672-1.5-1.5-1.5zm-10.563-5l-2.937-7h16.812l-1.977 7h-11.898zm11.233-5h-11.162l1.259 3h9.056l.847-3zm5.635-5l-3.432 12h-12.597l.839 2h13.239l3.474-12h1.929l.743-2h-4.195z"
                        />
                    </svg>
                ),
                url: "/drafts",
                onClick: null,
            });
        }
        items.push({
            title: t("menu.option.my-consignments"),
            icon: (
                <svg
                    width="24"
                    height="24"
                    xmlns="http://www.w3.org/2000/svg"
                    fillRule="evenodd"
                    clipRule="evenodd"
                >
                    <path
                        fill="currentColor"
                        d="M13.403 24h-13.403v-22h3c1.231 0 2.181-1.084 3-2h8c.821.916 1.772 2 3 2h3v9.15c-.485-.098-.987-.15-1.5-.15l-.5.016v-7.016h-4l-2 2h-3.897l-2.103-2h-4v18h9.866c.397.751.919 1.427 1.537 2zm5.097-11c3.035 0 5.5 2.464 5.5 5.5s-2.465 5.5-5.5 5.5c-3.036 0-5.5-2.464-5.5-5.5s2.464-5.5 5.5-5.5zm0 2c1.931 0 3.5 1.568 3.5 3.5s-1.569 3.5-3.5 3.5c-1.932 0-3.5-1.568-3.5-3.5s1.568-3.5 3.5-3.5zm2.5 4h-3v-3h1v2h2v1zm-15.151-4.052l-1.049-.984-.8.823 1.864 1.776 3.136-3.192-.815-.808-2.336 2.385zm6.151 1.052h-2v-1h2v1zm2-2h-4v-1h4v1zm-8.151-4.025l-1.049-.983-.8.823 1.864 1.776 3.136-3.192-.815-.808-2.336 2.384zm8.151 1.025h-4v-1h4v1zm0-2h-4v-1h4v1zm-5-6c0 .552.449 1 1 1 .553 0 1-.448 1-1s-.447-1-1-1c-.551 0-1 .448-1 1z"
                    />
                </svg>
            ),
            url: "/orders",
            onClick: null,
        });
        items.push({
            title: t("menu.option.spedition"),
            icon: (
                <svg
                    width="24"
                    height="24"
                    xmlns="http://www.w3.org/2000/svg"
                    fillRule="evenodd"
                    clipRule="evenodd"
                >
                    <path
                        fill="currentColor"
                        d="M5 11v1h8v-7h-10v-1c0-.552.448-1 1-1h10c.552 0 1 .448 1 1v2h4.667c1.117 0 1.6.576 1.936 1.107.594.94 1.536 2.432 2.109 3.378.188.312.288.67.288 1.035v4.48c0 1.089-.743 2-2 2h-1c0 1.656-1.344 3-3 3s-3-1.344-3-3h-4c0 1.656-1.344 3-3 3s-3-1.344-3-3h-1c-.552 0-1-.448-1-1v-6h-2v-2h7v2h-3zm3 5.8c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm10 0c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-3-2.8h-10v2h.765c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h5.53c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h1.765v-4.575l-1.711-2.929c-.179-.307-.508-.496-.863-.496h-4.426v6zm1-5v3h5l-1.427-2.496c-.178-.312-.509-.504-.868-.504h-2.705zm-16-3h8v2h-8v-2z"
                    />
                </svg>
            ),
            url: "/spedition",
            onClick: null,
        });
        if (context.user.customer.integrations?.length) {
            items.push({
                title: t("menu.option.imported-orders"),
                icon: (
                    <svg
                        width="24"
                        height="24"
                        xmlns="http://www.w3.org/2000/svg"
                        fillRule="evenodd"
                        clipRule="evenodd"
                    >
                        <path
                            fill="currentColor"
                            d="M20 24h-20v-22h3c1.229 0 2.18-1.084 3-2h8c.82.916 1.771 2 3 2h3v9h-2v-7h-4l-2 2h-3.898l-2.102-2h-4v18h16v-5h2v7zm-10-4h-6v-1h6v1zm0-2h-6v-1h6v1zm6-5h8v2h-8v3l-5-4 5-4v3zm-6 3h-6v-1h6v1zm0-2h-6v-1h6v1zm0-2h-6v-1h6v1zm0-2h-6v-1h6v1zm-1-7c0 .552.448 1 1 1s1-.448 1-1-.448-1-1-1-1 .448-1 1z"
                        />
                    </svg>
                ),
                url: "/orders/imported",
                onClick: null,
            });
        }

        items.push(
            {
                title: t("menu.option.book-pickup"),
                icon: (
                    <svg
                        width="24"
                        height="24"
                        xmlns="http://www.w3.org/2000/svg"
                        fillRule="evenodd"
                        clipRule="evenodd"
                    >
                        <path
                            fill="currentColor"
                            d="M2 11.741c-1.221-1.009-2-2.535-2-4.241 0-3.036 2.464-5.5 5.5-5.5 1.706 0 3.232.779 4.241 2h4.259c.552 0 1 .448 1 1v2h4.667c1.117 0 1.6.576 1.936 1.107.594.94 1.536 2.432 2.109 3.378.188.312.288.67.288 1.035v4.48c0 1.156-.616 2-2 2h-1c0 1.656-1.344 3-3 3s-3-1.344-3-3h-4c0 1.656-1.344 3-3 3s-3-1.344-3-3h-2c-.552 0-1-.448-1-1v-6.259zm6 6.059c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm10 0c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-7.207-11.8c.135.477.207.98.207 1.5 0 3.036-2.464 5.5-5.5 5.5-.52 0-1.023-.072-1.5-.207v4.207h1.765c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h5.53c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h1.765v-4.575l-1.711-2.929c-.179-.307-.508-.496-.863-.496h-4.426v6h-2v-9h-2.207zm5.207 4v3h5l-1.427-2.496c-.178-.312-.509-.504-.868-.504h-2.705zm-10.5-6c1.932 0 3.5 1.568 3.5 3.5s-1.568 3.5-3.5 3.5-3.5-1.568-3.5-3.5 1.568-3.5 3.5-3.5zm.5 3h2v1h-3v-3h1v2z"
                        />
                    </svg>
                ),
                url: "/pickup",
                onClick: null,
            },
            {
                title: t("menu.option.contacts"),
                icon: (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M22 13h-2v-4h2v4zm0-9h-2v4h2v-4zm0 10h-2v4h2v-4zm0 5h-2v4h2v-4zm-7.928-2.246l-.342-.496c-.513.296-1.125.567-2.129.567-1.713 0-2.865-1.099-2.865-2.736 0-2.857 3.199-3.379 4.605-2.536 1.312.822 1.264 2.789.292 3.542-.519.413-1.1.399-.912-.379 0 0 .606-2.244.678-2.486h-.681l-.137.475c-.139-.392-.472-.647-.954-.647-1.01 0-1.862 1.095-1.862 2.392 0 .865.458 1.401 1.189 1.401.643 0 .977-.362 1.183-.687-.15 1.451 2.863.78 2.863-1.761 0-1.622-1.314-2.8-3.129-2.8-2.481 0-3.871 1.574-3.871 3.536 0 1.835 1.232 3.258 3.512 3.258 1.26 0 2.061-.363 2.56-.643zm-1.713-3.49c0 .396-.134.915-.342 1.321-.506.989-1.531.982-1.531-.07 0-.393.134-.899.342-1.29.512-.962 1.531-.812 1.531.039zm-6.864-9.264h13.505v20h-14c-1.656 0-3-1.343-3-3v-18c0-1.657 1.344-3 3-3h17v2h-16.505c-1.376 0-1.376 2 0 2zm.505 18h11v-16h-11v16z"
                        />
                    </svg>
                ),
                url: "/contacts",
                onClick: null,
            },
            {
                // 	title: "Mina fakturor",
                // 	icon: <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path fill="currentColor" d="M7 22v-16h14v7.543c0 4.107-6 2.457-6 2.457s1.518 6-2.638 6h-5.362zm16-7.614v-10.386h-18v20h8.189c3.163 0 9.811-7.223 9.811-9.614zm-10 1.614h-4v-1h4v1zm6-4h-10v1h10v-1zm0-3h-10v1h10v-1zm1-7h-17v19h-2v-21h19v2z"/></svg>,
                // 	url: "/invoices",
                // 	onClick: null
                // },{
                title: t("menu.option.my-account"),
                icon: (
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                    >
                        <path
                            fill="currentColor"
                            d="M20.822 18.096c-3.439-.794-6.641-1.49-5.09-4.418 4.719-8.912 1.251-13.678-3.732-13.678-5.082 0-8.465 4.949-3.732 13.678 1.598 2.945-1.725 3.641-5.09 4.418-2.979.688-3.178 2.143-3.178 4.663l.005 1.241h1.995c0-3.134-.125-3.55 1.838-4.003 2.851-.657 5.543-1.278 6.525-3.456.359-.795.592-2.103-.338-3.815-2.058-3.799-2.578-7.089-1.423-9.026 1.354-2.275 5.426-2.264 6.767-.034 1.15 1.911.639 5.219-1.403 9.076-.91 1.719-.671 3.023-.31 3.814.99 2.167 3.707 2.794 6.584 3.458 1.879.436 1.76.882 1.76 3.986h1.995l.005-1.241c0-2.52-.199-3.975-3.178-4.663z"
                        />
                    </svg>
                ),
                url: "/account",
                onClick: null,
                // },{
                // 	title: "Mina upphämtningar",
                // 	icon: <svg width="24" height="24" xmlns="http://www.w3.org/2000/svg" fillRule="evenodd" clipRule="evenodd"><path fill="currentColor" d="M3 18h-2c-.552 0-1-.448-1-1v-13c0-.552.448-1 1-1h13c.552 0 1 .448 1 1v2h4.667c1.117 0 1.6.576 1.936 1.107.594.94 1.536 2.432 2.109 3.378.188.312.288.67.288 1.035v4.48c0 1.089-.743 2-2 2h-1c0 1.656-1.344 3-3 3s-3-1.344-3-3h-6c0 1.656-1.344 3-3 3s-3-1.344-3-3zm3-1.2c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm12 0c.662 0 1.2.538 1.2 1.2 0 .662-.538 1.2-1.2 1.2-.662 0-1.2-.538-1.2-1.2 0-.662.538-1.2 1.2-1.2zm-3-2.8h-13v2h1.765c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h7.53c.549-.614 1.347-1 2.235-1 .888 0 1.686.386 2.235 1h1.765v-4.575l-1.711-2.929c-.179-.307-.508-.496-.863-.496h-4.426v6zm-2-9h-11v7h11v-7zm3 4v3h5l-1.427-2.496c-.178-.312-.509-.504-.868-.504h-2.705z"/></svg>,
                // 	url: "/pickups",
                // 	onClick: null
            },
        );

        items.push({
            title: "Tillägg",
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                >
                    <path
                        fill="currentColor"
                        stroke="#000"
                        strokeWidth=".5px"
                        d="M13 20.45c0-.932-.575-1.357-1.109-1.357-.332 0-.672.156-.953.438-.284.296-.389.469-.786.469-.47 0-1.152-.534-1.152-1.5s.682-1.5 1.152-1.5c.396 0 .501.173.785.468.281.283.621.438.953.438.534 0 1.109-.425 1.109-1.357v-3.549h3.55c.536 0 .439.139.108.454-.261.245-.657.616-.657 1.394 0 1.039 1.004 2.152 2.5 2.152s2.5-1.113 2.5-2.152c0-.777-.396-1.148-.658-1.394-.333-.317-.425-.454.108-.454h3.55v-13h-24v24h13v-3.55zm0-19.45h10v11h-2.55c-.933 0-1.356.575-1.356 1.109 0 .332.155.672.438.953.294.284.468.388.468.786 0 .47-.534 1.152-1.5 1.152s-1.5-.682-1.5-1.152c0-.397.174-.501.469-.785.282-.281.438-.621.438-.953 0-.534-.424-1.109-1.356-1.109h-3.551v-3.551c0-.535.137-.44.454-.109.245.263.616.659 1.394.659 1.039 0 2.152-1.004 2.152-2.5s-1.113-2.5-2.152-2.5c-.777 0-1.148.396-1.394.659-.317.333-.454.424-.454-.109v-3.55zm-12 0h11v3.55c0 .932.575 1.357 1.109 1.357.463 0 .775-.252 1.075-.565.207-.221.32-.342.664-.342.47 0 1.152.534 1.152 1.5s-.682 1.5-1.152 1.5c-.344 0-.457-.121-.663-.341-.302-.314-.612-.565-1.075-.565-.535-.001-1.11.424-1.11 1.356v3.55h-3.55c-.536 0-.439-.139-.108-.454.262-.245.658-.616.658-1.394 0-1.039-1.004-2.152-2.5-2.152s-2.5 1.113-2.5 2.152c0 .777.396 1.148.658 1.394.333.317.425.454-.108.454h-3.55v-11zm11 22h-11v-10h3.55c.933 0 1.356-.575 1.356-1.109 0-.463-.25-.773-.564-1.075-.221-.207-.342-.32-.342-.664 0-.47.534-1.152 1.5-1.152s1.5.682 1.5 1.152c0 .344-.121.457-.342.663-.314.302-.564.612-.564 1.075 0 .535.424 1.11 1.356 1.11h3.55v3.55c0 .535-.137.44-.454.109-.245-.263-.616-.659-1.394-.659-1.039 0-2.152 1.004-2.152 2.5s1.113 2.5 2.152 2.5c.777 0 1.148-.396 1.394-.659.317-.333.454-.424.454.109v2.55z"
                    />
                </svg>
            ),
            url: "/extensions",
            onClick: null,
            alert: !!context?.integrationsExpiry?.length,
        });

        items.push({
            title: t("menu.option.tickets"),
            badge: context.ticketNotificationCount,
            icon: (
                <svg
                    width="24"
                    height="24"
                    xmlns="http://www.w3.org/2000/svg"
                    fillRule="evenodd"
                    clipRule="evenodd"
                >
                    <path
                        fill="currentColor"
                        d="M3.848 19h-.848c-.796 0-1.559-.316-2.121-.879-.563-.562-.879-1.325-.879-2.121v-3c0-7.175 5.377-13 12-13s12 5.825 12 13v3c0 .796-.316 1.559-.879 2.121-.562.563-1.325.879-2.121.879h-.848c-2.69 4.633-6.904 5-8.152 5-1.248 0-5.462-.367-8.152-5zm16.152-5.876c-.601.236-1.269-.18-1.269-.797 0-.304-.022-.61-.053-.915-1.761-.254-3.618-1.926-3.699-3.723-1.315 2.005-4.525 4.17-7.044 4.17 1.086-.699 1.839-2.773 1.903-3.508-.581 1.092-2.898 3.136-4.551 3.487l-.018.489c0 .619-.669 1.032-1.269.797v3.771c.287.256.632.464 1.041.594.225.072.412.224.521.424 2.206 4.046 5.426 4.087 6.438 4.087.929 0 3.719-.035 5.877-3.169-1.071.433-2.265.604-3.759.653-.37.6-1.18 1.016-2.118 1.016-1.288 0-2.333-.784-2.333-1.75s1.045-1.75 2.333-1.75c.933 0 1.738.411 2.112 1.005 1.9-.026 4.336-.334 5.888-2.645v-2.236zm-11-.624c.686 0 1.243.672 1.243 1.5s-.557 1.5-1.243 1.5-1.243-.672-1.243-1.5.557-1.5 1.243-1.5zm6 0c.686 0 1.243.672 1.243 1.5s-.557 1.5-1.243 1.5-1.243-.672-1.243-1.5.557-1.5 1.243-1.5zm5.478-1.5h1.357c-.856-5.118-4.937-9-9.835-9-4.898 0-8.979 3.882-9.835 9h1.357c.52-4.023 3.411-7.722 8.478-7.722s7.958 3.699 8.478 7.722z"
                    />
                </svg>
            ),
            url: "/tickets",
            onClick: null,
        });

        const items2 = [];

        if (
            context.user.administrator ||
            (context.user.brand && context.user.brand.id)
        ) {
            if (adminViewUrl) {
                items2.push({
                    title: t("menu.option.administration-view"),
                    icon: (
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                        >
                            <path
                                fill="currentColor"
                                d="M22 3v18h-5v-2h3v-14h-6v3l-5.25-4 5.25-4v3h8zm-12 16h-6v-14h3v-2h-5v18h8v3l5.25-4-5.25-4v3z"
                            />
                        </svg>
                    ),
                    url: adminViewUrl,
                    onClick: () => {
                        context.setLayout("administrator");
                    },
                });
            }
        }

        items2.push({
            title: t("menu.option.logout"),
            icon: (
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                >
                    <path
                        fill="currentColor"
                        d="M10 9.408l2.963 2.592-2.963 2.592v-1.592h-8v-2h8v-1.592zm-2-4.408v4h-8v6h8v4l8-7-8-7zm6-3c-1.787 0-3.46.474-4.911 1.295l.228.2 1.396 1.221c1.004-.456 2.114-.716 3.287-.716 4.411 0 8 3.589 8 8s-3.589 8-8 8c-1.173 0-2.283-.26-3.288-.715l-1.396 1.221-.228.2c1.452.82 3.125 1.294 4.912 1.294 5.522 0 10-4.477 10-10s-4.478-10-10-10z"
                    />
                </svg>
            ),
            url: "/logout",
            onClick: null,
        });

        setMenuitems(items);
        setBottomItems(items2);
    }, [context]);

    return (
        <Container>
            <Header className="c-header" />
            <MenuWrapper>
                <div className="top">
                    {menuItems.map((item, index) => {
                        let isSelected = false;
                        if (item.url === "/") {
                            isSelected =
                                item.url === props.history.location.pathname;
                        } else {
                            isSelected =
                                props.history.location.pathname.indexOf(
                                    item.url,
                                ) === 0;
                        }
                        const editingDraft =
                            props.history.location.pathname.match(
                                /^\/create\/\d+$/g,
                            );
                        if (editingDraft) {
                            isSelected = item.url === "/drafts";
                        }
                        if (
                            props.history.location.pathname ===
                            "/orders/imported"
                        ) {
                            if (item.url === "/orders") {
                                isSelected = false;
                            }
                        }
                        return (
                            <Item
                                key={item.key || index}
                                href={item.url}
                                onClick={(ev) => {
                                    if (item.url) {
                                        props.history.push(item.url);
                                    }
                                    if (item.onClick) {
                                        item.onClick();
                                    }
                                    ev.preventDefault();
                                    return false;
                                }}
                                className={isSelected ? " is-selected" : ""}
                            >
                                <div className="badge">
                                    <Badge badge={item.badge} />
                                </div>
                                <div className="icon">{item.icon}</div>
                                {item.title && (
                                    <div className="title">
                                        <div>{item.title}</div>
                                        <div
                                            className={"absolute right-4 top-4"}
                                        >
                                            {item.alert && (
                                                <Alert
                                                    type={
                                                        context?.integrationsExpiry?.some(
                                                            (integration) =>
                                                                integration.status ===
                                                                EXPIRED,
                                                        )
                                                            ? AlertTypes.ERROR
                                                            : AlertTypes.WARNING
                                                    }
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                                {item.content && item.content}
                            </Item>
                        );
                    })}
                </div>
                <PortalMessages />
                <div className="bottom">
                    {bottomItems.map((item, index) => {
                        let isSelected = false;
                        if (item.url === "/") {
                            isSelected =
                                item.url === props.history.location.pathname;
                        } else {
                            isSelected =
                                props.history.location.pathname.indexOf(
                                    item.url,
                                ) === 0;
                        }
                        const editingDraft =
                            props.history.location.pathname.match(
                                /^\/create\/\d+$/g,
                            );
                        if (editingDraft) {
                            isSelected = item.url === "/drafts";
                        }
                        if (
                            props.history.location.pathname ===
                            "/orders/imported"
                        ) {
                            if (item.url === "/orders") {
                                isSelected = false;
                            }
                        }
                        return (
                            <Item
                                key={item.key || index}
                                href={item.url}
                                onClick={(ev) => {
                                    if (item.url) {
                                        props.history.push(item.url);
                                    }
                                    if (item.onClick) {
                                        item.onClick();
                                    }
                                    ev.preventDefault();
                                    return false;
                                }}
                                className={isSelected ? " is-selected" : ""}
                            >
                                <div className="badge">
                                    <Badge badge={item.badge} />
                                </div>
                                <div className="icon">{item.icon}</div>
                                {item.title && (
                                    <div className="title">{item.title}</div>
                                )}
                                {item.content && item.content}
                            </Item>
                        );
                    })}
                </div>
            </MenuWrapper>
            <Content>{props.children}</Content>
        </Container>
    );
}

export default LoggedIn;
